import { createSelector } from "@reduxjs/toolkit"
import { memoize } from "nodash"
import { sortByDbscanClusters } from "@nova/calculate-geospatial"
import {
    HotspotGraphData,
    HotspotStatus,
    HotspotWithContext,
    HotspotWithDistance,
    HotspotWithLabel,
} from "@nova/constants"
import { getHotspotPerimeterDistances } from "@nova/data-create-hotspots-kml"
import {
    filterHotspotsByDistance,
    filterHotspotsByGraphs,
    filterHotspotsBySensitivity,
    sortHotspotsAndAssignIds,
} from "@nova/util-hotspot"
import { WeakObj } from "@nova/weak-obj"
import store from "integrations/redux/store"
import {
    selectActiveFirePerimeterIds,
    selectActiveFirePerimeterPolygons,
} from "../fire-perimeters/selectors"
import {
    selectMergedFlightById,
    selectMergedFlights,
    selectUnmergedNonArchivedInfraredFlights,
} from "../mergedFlights/selectors"
import {
    computeSizeGraph,
    computeThermalGraph,
} from "features/map/inspector-panel/hotspots/filters/module"
import {
    createInfraredFlightData,
    selectFlightsWithRemovedHotspots,
    selectHoveredBoundingBoxId,
    selectInfraredFlightById,
    selectInfraredFlightDataById,
    selectInfraredFlightPerimeterRange,
    selectInfraredFlights,
} from "../flights/selectors"

export const selectMergedSiblingFlightIds = memoize((flightId: string) =>
    createSelector(
        [selectInfraredFlightById(flightId), selectInfraredFlights],
        (flight, flights) => {
            if (!flight) return []
            if (!flight.mergeId) return [flight.id]
            return flights.filter((f) => f.mergeId === flight.mergeId).map((f) => f.id)
        }
    )
)

export const selectHotspotPropertiesByFlightId = memoize((id: string) => {
    const weakObj = new WeakObj(createInfraredFlightData(), [
        "sensitivity",
        "perimeterDistance",
        "pointColor",
        "pointType",
        "polyFill",
        "polyStroke",
        "orthoGradient",
        "filters",
    ])
    return createSelector([selectInfraredFlightDataById(id)], (flight) =>
        flight ? weakObj.refFrom(flight) : {}
    )
})

export const selectHotspotsByFlightId = memoize((id: string) => {
    let hotspots = new WeakRef({})
    let name = ""
    let hotspotsWithContext: HotspotWithContext[] = []

    return createSelector([selectInfraredFlightDataById(id)], (flight) => {
        if (!flight || (hotspots.deref() === flight.hotspots && name === flight.name)) {
            return hotspotsWithContext
        }
        hotspots = new WeakRef(flight.hotspots)
        name = flight.name
        const result = sortHotspotsAndAssignIds(flight)
        hotspotsWithContext = result
        return result
    })
})

export const selectNonRemovedHotspotImageIdMap = memoize((flightId: string) =>
    createSelector(
        [selectMapHotspots, selectMergedSiblingFlightIds(flightId)],
        (hotspots, flightIds) => {
            const map = Object.values(hotspots)
                .filter((hotspot) => flightIds.includes(hotspot.flightId))
                .reduce<Map<string, HotspotWithLabel[]>>((prev, curr) => {
                    const arr = prev.get(curr.imageId)
                    if (arr) {
                        arr.push(curr)
                    } else {
                        prev.set(curr.imageId, [curr])
                    }
                    return prev
                }, new Map())

            return map
        }
    )
)

export const selectRemovedHotspotsImageIdMap = memoize((flightId: string) =>
    createSelector([selectMergedSiblingFlightIds(flightId)], (flightIds) => {
        const state = store.getState()

        const hotspots = flightIds.flatMap((flightId) =>
            selectSortedAndFilteredRemovedHotspotsByFlightId(flightId)(state)
        )

        return hotspots.reduce((prev, curr) => {
            const arr = prev.get(curr.imageId)
            if (arr) {
                arr.push(curr)
            } else {
                prev.set(curr.imageId, [curr])
            }
            return prev
        }, new Map<string, HotspotWithLabel[]>())
    })
)

export const selectHotspotDistancesToPerimeterMapByFlightId = memoize((flightId: string) =>
    createSelector(
        [
            selectHotspotsByFlightId(flightId),
            selectActiveFirePerimeterPolygons,
            selectInfraredFlightPerimeterRange(flightId),
        ],
        (hotspots, polygons, flight) => {
            if (!flight?.perimeterDistance?.range.length) return {}
            const distances = getHotspotPerimeterDistances(hotspots, polygons) || []
            return hotspots.reduce(
                (acc, cur, i) => {
                    acc[cur.id] = distances[i]
                    return acc
                },
                {} as Record<string, number | undefined>
            )
        }
    )
)

// All filters intersected.
// export const selectFilteredHotspotsByFlightId = memoize((flightId: string) =>
//     createSelector(
//         [
//             selectInfraredFlightById(flightId),
//             selectHotspotPropertiesByFlightId(flightId),
//             selectHotspotsByFlightId(flightId),
//             selectHotspotDistancesToPerimeterMapByFlightId(flightId),
//         ],
//         (flight, properties, hotspots, distances) => {
//             const distanceToPerimeterIsEnabled =
//                 flight.filters?.perimeter !== undefined
//                     ? flight.filters.perimeter
//                     : (flight.perimeterDistance?.range.length || 0) > 0
//             const sizeRange = flight.filters?.size
//             return filterHotspots(
//                 properties,
//                 hotspots,
//                 distances,
//                 distanceToPerimeterIsEnabled,
//                 sizeRange
//             )
//         }
//     )
// )

// Hotspots filtered by just sensitivity.
export const selectSensitivityFilteredHotspotsByFlightId = memoize((flightId: string) =>
    createSelector(
        [selectHotspotPropertiesByFlightId(flightId), selectHotspotsByFlightId(flightId)],
        (properties, hotspots) => {
            return filterHotspotsBySensitivity({ sensitivity: properties.sensitivity, hotspots })
        }
    )
)

// Hotspots filtered by just sensitivity AND distance to perimeter.
export const selectSensitivityAndDistanceFilteredHotspotsByFlightId = memoize((flightId: string) =>
    createSelector(
        [
            selectSensitivityFilteredHotspotsByFlightId(flightId),
            selectHotspotPropertiesByFlightId(flightId),
            selectHotspotDistancesToPerimeterMapByFlightId(flightId),
            selectActiveFirePerimeterIds,
        ],
        (hotspots, properties, distances, activeFirePerimeterIds) => {
            const distanceToPerimeterIsEnabled =
                (properties?.filters?.perimeter !== undefined
                    ? properties.filters.perimeter
                    : (properties?.perimeterDistance?.range.length || 0) > 0) &&
                activeFirePerimeterIds.length > 0

            return filterHotspotsByDistance({
                distances: distanceToPerimeterIsEnabled ? distances : {},
                properties,
                distanceToPerimeterIsEnabled,
                hotspots,
            })
        }
    )
)

export const selectHotspotsThermalGraphByFlightId = memoize((flightId: string) =>
    createSelector(
        [selectSensitivityAndDistanceFilteredHotspotsByFlightId(flightId)],
        (hotspotsFilteredBySensitivityAndDistance): HotspotGraphData =>
            computeThermalGraph({ hotspotsFilteredBySensitivityAndDistance })
    )
)

export const selectHotspotsSizeGraphByFlightId = memoize((flightId: string) =>
    createSelector(
        [selectSensitivityAndDistanceFilteredHotspotsByFlightId(flightId)],
        (hotspotsFilteredBySensitivityAndDistance): HotspotGraphData =>
            computeSizeGraph({
                hotspotsFilteredBySensitivityAndDistance,
                percentiles: { lower: 0, upper: 0.85 },
            })
    )
)

export const selectFilteredHotspotsByFlightId = memoize((flightId: string) =>
    createSelector(
        [
            selectSensitivityFilteredHotspotsByFlightId(flightId),
            selectSensitivityAndDistanceFilteredHotspotsByFlightId(flightId),
            selectHotspotPropertiesByFlightId(flightId),
        ],
        (hotspotsFilteredBySensitivity, hotspotsFilteredBySensitivityAndDistance, properties) => {
            return filterHotspotsByGraphs({
                hotspotsFilteredBySensitivity,
                hotspotsFilteredBySensitivityAndDistance,
                sizeRange: properties.filters?.size,
                temperatureRange: properties.filters?.temperature,
            })
        }
    )
)

export const selectHotspotsContextMap = createSelector(
    [selectUnmergedNonArchivedInfraredFlights, selectMergedFlights],
    (flights, mergedFlights) => {
        const state = store.getState()
        const map = {} as Record<string, HotspotWithLabel>
        flights.forEach((flight) => {
            selectAllSortedAndFilteredHotspotsByFlightId(flight.id)(state).forEach(
                (hotspot) => (map[hotspot.id] = hotspot)
            )
        })
        mergedFlights.forEach((flight) => {
            selectSortedAndFilteredMergedHotspotsByFlightId(flight.id)(state).forEach(
                (hotspot) => (map[hotspot.id] = hotspot)
            )
        })
        return map
    }
)
let lastMap: Record<string, HotspotWithLabel> = {}
export const selectMapHotspots = createSelector(
    [selectUnmergedNonArchivedInfraredFlights, selectMergedFlights],
    (flights, mergedFlights) => {
        const state = store.getState()
        const map = {} as Record<string, HotspotWithLabel>
        flights.forEach((flight) => {
            selectSortedAndFilteredHotspotsByFlightId(flight.id)(state).forEach(
                (hotspot) => (map[hotspot.id] = hotspot)
            )
        })
        mergedFlights.forEach((flight) => {
            selectSortedAndFilteredMergedHotspotsByFlightId(flight.id)(state).forEach(
                (hotspot) => (map[hotspot.id] = hotspot)
            )
        })

        const keys = Object.keys(map)
        if (
            keys.length === Object.keys(lastMap).length &&
            keys.every((k) => lastMap[k] === map[k])
        ) {
            return lastMap
        }
        lastMap = map
        return map
    }
)

export const selectHotspotWithLabelById = memoize((id: string) =>
    createSelector([selectHotspotsContextMap], (hotspots) => hotspots[id])
)

export const selectHoveredInfraredFlightId = createSelector(
    [selectUnmergedNonArchivedInfraredFlights, selectMergedFlights, selectHoveredBoundingBoxId],
    (flights, mergedFlights, id) =>
        [...flights, ...mergedFlights].some((flight) => flight.id === id) ? id : undefined
)

const selectAllSortedAndFilteredHotspotsByFlightId = memoize((flightId: string) => {
    return createSelector([selectFilteredHotspotsByFlightId(flightId)], (hotspots) => {
        const sorted = sortByDbscanClusters(hotspots)

        let nonRemovedIncrementor = 1
        let removedIncrementor = 1
        const labelled = sorted.map((hotspot) => ({
            ...hotspot,
            label: `${hotspot.status === HotspotStatus.Removed ? removedIncrementor++ : nonRemovedIncrementor++}`,
        }))
        return labelled as HotspotWithLabel[]
    })
})

export const selectSortedAndFilteredHotspotsByFlightId = memoize((flightId: string) => {
    return createSelector([selectAllSortedAndFilteredHotspotsByFlightId(flightId)], (hotspots) => {
        return hotspots.filter((hotspot) => hotspot.status !== HotspotStatus.Removed)
    })
})

export const selectSortedAndFilteredRemovedHotspotsByFlightId = memoize((flightId: string) =>
    createSelector([selectAllSortedAndFilteredHotspotsByFlightId(flightId)], (hotspots) => {
        return hotspots.filter((hotspot) => hotspot.status === HotspotStatus.Removed)
    })
)

export const selectRemovedHotspots = createSelector(
    [selectFlightsWithRemovedHotspots],
    (flightsMap) => {
        const state = store.getState()
        return Object.entries(flightsMap)
            .filter(([, val]) => val)
            .flatMap(([id]) => selectSortedAndFilteredRemovedHotspotsByFlightId(id)(state))
    }
)

export const selectSortedAndFilteredMergedHotspotsByFlightId = memoize((flightId: string) =>
    createSelector([selectMergedFlightById(flightId)], (mergedFlight) => {
        const state = store.getState()

        const hotspotIds = mergedFlight?.hotspotFlightIds as string[]

        let hotspots: HotspotWithDistance[] = []
        if (!!hotspotIds) {
            hotspots = hotspotIds.flatMap((id) => selectFilteredHotspotsByFlightId(id)(state))
        }
        const sorted = sortByDbscanClusters(hotspots)
        const filtered = sorted.filter((hotspot) => hotspot.status !== HotspotStatus.Removed)
        const labelled = filtered.map((hotspot, i) => ({ ...hotspot, label: `${i + 1}` }))
        return labelled as HotspotWithLabel[]
    })
)

export const selectImagePreviewHotspots = (id: string, mergeId = "") => {
    if (mergeId) {
        return selectSortedAndFilteredMergedHotspotsByFlightId(mergeId)
    } else {
        return selectSortedAndFilteredHotspotsByFlightId(id)
    }
}
